import React, { useState, useEffect, useCallback } from 'react'
import { Row, Col, Card, Button, Modal, Form, Input, Select, DatePicker, Tag, Spin, message, Dropdown, Empty, Badge, Avatar, Tooltip, Divider } from 'antd'
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    CheckOutlined,
    ClockCircleOutlined,
    UserOutlined,
    FlagOutlined,
    MoreOutlined,
    FilterOutlined,
    CalendarOutlined,
    BellOutlined,
    EyeOutlined,
    DragOutlined
} from '@ant-design/icons'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import 'dayjs/locale/ru'
import api from '../services/api'
import { useAuthStore } from '../store/authStore'
import { useSearchParams } from 'react-router-dom'

dayjs.extend(relativeTime)
dayjs.locale('ru')

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: JSX.Element }> = {
    pending: { label: 'Ожидает', color: '#8b5cf6', icon: <ClockCircleOutlined /> },
    in_progress: { label: 'В работе', color: '#3b82f6', icon: <FlagOutlined /> },
    completed: { label: 'Завершено', color: '#10b981', icon: <CheckOutlined /> },
    cancelled: { label: 'Отменено', color: '#6b7280', icon: <DeleteOutlined /> }
}

const PRIORITY_CONFIG: Record<string, { label: string; color: string }> = {
    low: { label: 'Низкий', color: '#10b981' },
    medium: { label: 'Средний', color: '#f59e0b' },
    high: { label: 'Высокий', color: '#f97316' },
    urgent: { label: 'Срочно', color: '#ef4444' }
}

interface Task {
    id: number
    title: string
    description?: string
    status: string
    priority: string
    created_by_id: number
    created_by_name?: string
    assigned_to_id?: number
    assigned_to_name?: string
    deadline?: string
    reminder_at?: string
    reminder_sent: boolean
    completed_at?: string
    created_at: string
    updated_at: string
}

export default function Tasks() {
    const [tasks, setTasks] = useState<Task[]>([])
    const [loading, setLoading] = useState(false)
    const [modalVisible, setModalVisible] = useState(false)
    const [viewModalVisible, setViewModalVisible] = useState(false)
    const [selectedTask, setSelectedTask] = useState<Task | null>(null)
    const [editingTask, setEditingTask] = useState<Task | null>(null)
    const [operators, setOperators] = useState<any[]>([])
    const [filterPriority, setFilterPriority] = useState<string | null>(null)
    const [draggedTask, setDraggedTask] = useState<Task | null>(null)
    const [dragOverStatus, setDragOverStatus] = useState<string | null>(null)
    const [dragPosition, setDragPosition] = useState<{ x: number; y: number } | null>(null)
    const [highlightedTaskId, setHighlightedTaskId] = useState<number | null>(null)
    const [form] = Form.useForm()
    const { user } = useAuthStore()
    const [searchParams] = useSearchParams()

    // Ref for double-click detection - must be at parent level to persist across re-renders
    const lastClickRef = React.useRef<{ taskId: number; time: number } | null>(null)
    const openedTaskFromUrlRef = React.useRef<number | null>(null)

    const loadTasks = useCallback(async () => {
        setLoading(true)
        try {
            const params: any = {}
            if (filterPriority) params.priority = filterPriority

            const response = await api.get('/tasks', { params })
            setTasks(response.data)
        } catch (error) {
            console.error('Failed to load tasks:', error)
        } finally {
            setLoading(false)
        }
    }, [filterPriority])

    const loadOperators = async () => {
        try {
            const response = await api.get('/operators')
            setOperators(response.data)
        } catch (error) {
            console.error('Failed to load operators:', error)
        }
    }

    useEffect(() => {
        loadTasks()
        loadOperators()
    }, [loadTasks])

    useEffect(() => {
        const taskIdParam = searchParams.get('taskId')
        const taskId = taskIdParam ? Number(taskIdParam) : null
        if (!taskId || Number.isNaN(taskId) || openedTaskFromUrlRef.current === taskId) return

        const task = tasks.find((item) => item.id === taskId)
        if (!task) {
            if (filterPriority) setFilterPriority(null)
            return
        }

        openedTaskFromUrlRef.current = taskId
        setHighlightedTaskId(taskId)
        openViewModal(task)

        setTimeout(() => {
            const taskElement = document.querySelector(`[data-task-id="${taskId}"]`) as HTMLElement | null
            taskElement?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }, 100)

        setTimeout(() => {
            setHighlightedTaskId((current) => current === taskId ? null : current)
        }, 2500)
    }, [searchParams, tasks, filterPriority])

    const handleCreateOrUpdate = async (values: any) => {
        try {
            const data = {
                ...values,
                deadline: values.deadline?.toISOString(),
                reminder_at: values.reminder_at?.toISOString()
            }

            if (editingTask) {
                await api.put(`/tasks/${editingTask.id}`, data)
                message.success('Задача обновлена')
            } else {
                await api.post('/tasks', data)
                message.success('Задача создана')
            }

            setModalVisible(false)
            setEditingTask(null)
            form.resetFields()
            loadTasks()
        } catch (error) {
            message.error('Ошибка сохранения')
        }
    }

    const handleDelete = async (id: number) => {
        try {
            await api.delete(`/tasks/${id}`)
            message.success('Задача удалена')
            loadTasks()
        } catch (error) {
            message.error('Ошибка удаления')
        }
    }

    const handleStatusChange = async (task: Task, newStatus: string) => {
        try {
            await api.put(`/tasks/${task.id}`, { status: newStatus })
            message.success('Статус обновлен')
            loadTasks()
        } catch (error) {
            message.error('Ошибка обновления')
        }
    }

    const openEditModal = (task: Task) => {
        setEditingTask(task)
        form.setFieldsValue({
            ...task,
            deadline: task.deadline ? dayjs(task.deadline) : null,
            reminder_at: task.reminder_at ? dayjs(task.reminder_at) : null
        })
        setModalVisible(true)
    }

    const openViewModal = (task: Task) => {
        setSelectedTask(task)
        setViewModalVisible(true)
    }

    const getTasksByStatus = (status: string) => {
        return tasks.filter((t: Task) => t.status === status)
    }

    // Custom mouse-based drag handlers with threshold to distinguish click from drag
    const [isPotentialDrag, setIsPotentialDrag] = useState<{ task: Task; startX: number; startY: number } | null>(null)
    const DRAG_THRESHOLD = 8 // pixels - must move this far to start drag

    const handleMouseDragStart = (task: Task, e: React.MouseEvent) => {
        // Don't start drag immediately - just record potential drag
        setIsPotentialDrag({ task, startX: e.clientX, startY: e.clientY })
    }

    const handleMouseDragEnd = () => {
        setIsPotentialDrag(null)
        setDraggedTask(null)
        setDragOverStatus(null)
        setDragPosition(null)
    }

    // Global mouse move tracking - activate drag only after threshold
    useEffect(() => {
        if (!isPotentialDrag && !draggedTask) return

        const handleMouseMove = (e: MouseEvent) => {
            // Check if we should activate drag (threshold reached)
            if (isPotentialDrag && !draggedTask) {
                const dx = e.clientX - isPotentialDrag.startX
                const dy = e.clientY - isPotentialDrag.startY
                const distance = Math.sqrt(dx * dx + dy * dy)

                if (distance >= DRAG_THRESHOLD) {
                    // Threshold reached - activate drag!
                    setDraggedTask(isPotentialDrag.task)
                    setDragPosition({ x: e.clientX, y: e.clientY })
                }
            } else if (draggedTask) {
                // Already dragging - update position
                setDragPosition({ x: e.clientX, y: e.clientY })
            }
        }

        const handleMouseUp = () => {
            // Cleanup everything
            setIsPotentialDrag(null)
            setDraggedTask(null)
            setDragOverStatus(null)
            setDragPosition(null)
        }

        document.addEventListener('mousemove', handleMouseMove)
        document.addEventListener('mouseup', handleMouseUp)

        return () => {
            document.removeEventListener('mousemove', handleMouseMove)
            document.removeEventListener('mouseup', handleMouseUp)
        }
    }, [isPotentialDrag, draggedTask])

    const handleColumnMouseEnter = (status: string) => {
        if (draggedTask) {
            setDragOverStatus(status)
        }
    }

    const handleColumnMouseLeave = () => {
        setDragOverStatus(null)
    }

    const handleColumnMouseUp = async (newStatus: string) => {
        if (draggedTask && draggedTask.status !== newStatus) {
            await handleStatusChange(draggedTask, newStatus)
        }
        setIsPotentialDrag(null)
        setDraggedTask(null)
        setDragOverStatus(null)
        setDragPosition(null)
    }

    // Kanban Column Component
    const KanbanColumn = ({ status, config }: { status: string, config: typeof STATUS_CONFIG.pending }) => {
        const columnTasks = getTasksByStatus(status)
        const isDropTarget = dragOverStatus === status

        return (
            <div
                onMouseEnter={() => handleColumnMouseEnter(status)}
                onMouseLeave={handleColumnMouseLeave}
                onMouseUp={() => handleColumnMouseUp(status)}
                style={{
                    background: isDropTarget ? 'rgba(102, 126, 234, 0.1)' : 'var(--bg-surface)',
                    borderRadius: 16,
                    border: isDropTarget ? '2px dashed #667eea' : '1px solid var(--border)',
                    minHeight: 500,
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.2s ease'
                }}
            >
                {/* Column Header */}
                <div style={{
                    padding: '16px 20px',
                    borderBottom: '1px solid var(--border)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            background: config.color
                        }} />
                        <span style={{
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            fontSize: 15
                        }}>
                            {config.label}
                        </span>
                    </div>
                    <Badge
                        count={columnTasks.length}
                        style={{ backgroundColor: config.color }}
                    />
                </div>

                {/* Tasks */}
                <div style={{ padding: 12, flex: 1, overflowY: 'auto' }}>
                    {columnTasks.length === 0 ? (
                        <Empty
                            description="Нет задач"
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            style={{ opacity: 0.5 }}
                        />
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {columnTasks.map((task: Task) => (
                                <TaskCard
                                    key={task.id}
                                    task={task}
                                    onView={() => openViewModal(task)}
                                    onEdit={() => openEditModal(task)}
                                    onDelete={() => handleDelete(task.id)}
                                    onStatusChange={(s: string) => handleStatusChange(task, s)}
                                    onMouseDragStart={(e) => handleMouseDragStart(task, e)}
                                    onMouseDragEnd={handleMouseDragEnd}
                                    isDragging={draggedTask?.id === task.id}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        )
    }

    // Task Card Component
    const TaskCard = ({
        task,
        onView,
        onEdit,
        onDelete,
        onStatusChange,
        onMouseDragStart,
        onMouseDragEnd,
        isDragging
    }: {
        task: Task
        onView: () => void
        onEdit: () => void
        onDelete: () => void
        onStatusChange: (status: string) => void
        onMouseDragStart: (e: React.MouseEvent) => void
        onMouseDragEnd: () => void
        isDragging: boolean
    }) => {
        const priorityConfig = PRIORITY_CONFIG[task.priority as keyof typeof PRIORITY_CONFIG]
        // Only mark as overdue if past deadline AND not completed
        const isOverdue = task.deadline && dayjs(task.deadline).isBefore(dayjs()) && task.status !== 'completed'

        const menuItems = [
            { key: 'view', label: 'Просмотреть', icon: <EyeOutlined /> },
            { key: 'edit', label: 'Редактировать', icon: <EditOutlined /> },
            { type: 'divider' as const },
            ...Object.entries(STATUS_CONFIG)
                .filter(([key]) => key !== task.status)
                .map(([key, cfg]) => ({
                    key,
                    label: cfg.label,
                    icon: cfg.icon
                })),
            { type: 'divider' as const },
            { key: 'delete', label: 'Удалить', icon: <DeleteOutlined />, danger: true }
        ]

        const handleMenuClick = ({ key }: { key: string }) => {
            if (key === 'view') onView()
            else if (key === 'edit') onEdit()
            else if (key === 'delete') onDelete()
            else onStatusChange(key)
        }

        const handleCardMouseDown = (e: React.MouseEvent) => {
            // Don't start drag if clicking on interactive elements (button, dropdown, menu items)
            const target = e.target as HTMLElement
            if (target.closest('button') || target.closest('.ant-dropdown-trigger') || target.closest('[role="button"]') || target.closest('.ant-dropdown-menu-item') || target.closest('.ant-dropdown-menu')) {
                return
            }

            // Check for double-click (two clicks within 300ms on same task)
            // Use the ref from parent component (passed via closure from Tasks)
            const now = Date.now()
            const lastClick = (window as any).__taskLastClick as { taskId: number; time: number } | null
            if (lastClick &&
                lastClick.taskId === task.id &&
                now - lastClick.time < 300) {
                // Double-click detected - open edit
                (window as any).__taskLastClick = null
                onEdit()
                return
            }

            // Record this click
            (window as any).__taskLastClick = { taskId: task.id, time: now }

            // Only start drag with left mouse button
            if (e.button === 0) {
                onMouseDragStart(e);
            }
        }

        return (
            <div
                data-task-id={task.id}
                onMouseDown={handleCardMouseDown}
                onMouseUp={() => {
                    onMouseDragEnd();
                }}
                className={isOverdue ? 'task-card-overdue' : 'task-card-sticky'}
                style={{
                    borderRadius: 12,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    cursor: isDragging ? 'grabbing' : 'grab',
                    marginBottom: 8,
                    userSelect: 'none',
                    padding: 14,
                    background: '#FFF3B0',
                    border: '1px solid rgba(230, 190, 60, 0.5)',
                    outline: highlightedTaskId === task.id ? '3px solid rgba(0,136,204,0.45)' : 'none',
                    outlineOffset: 2,
                    opacity: isDragging ? 0.5 : 1,
                    transform: isDragging ? 'scale(0.98)' : 'none',
                    transition: 'opacity 0.2s, transform 0.2s'
                }}
            >
                {/* Drag indicator & Priority & Actions */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: 8
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, pointerEvents: 'none' }}>
                        <DragOutlined style={{ color: '#666', cursor: 'grab' }} />
                        <Tag
                            color={priorityConfig.color}
                            style={{
                                borderRadius: 6,
                                fontSize: 11,
                                margin: 0
                            }}
                        >
                            {priorityConfig.label}
                        </Tag>
                    </div>
                    <div style={{ pointerEvents: 'auto' }}>
                        <Dropdown
                            menu={{ items: menuItems, onClick: handleMenuClick }}
                            trigger={['click']}
                        >
                            <Button
                                type="text"
                                size="small"
                                icon={<MoreOutlined />}
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                                style={{ color: '#666' }}
                            />
                        </Dropdown>
                    </div>
                </div>

                {/* Title */}
                <div style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#333333',
                    marginBottom: 8,
                    lineHeight: 1.4,
                    pointerEvents: 'none'
                }}>
                    {task.title}
                </div>

                {/* Description */}
                {task.description && (
                    <div style={{
                        fontSize: 12,
                        color: 'var(--text-secondary)',
                        marginBottom: 10,
                        lineHeight: 1.5,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        pointerEvents: 'none'
                    }}>
                        {task.description}
                    </div>
                )}

                {/* Footer */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: 8,
                    paddingTop: 8,
                    borderTop: '1px solid var(--border)',
                    pointerEvents: 'none'
                }}>
                    {/* Deadline */}
                    {task.deadline ? (
                        <Tooltip title={dayjs(task.deadline).format('DD.MM.YYYY HH:mm')}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4,
                                color: isOverdue ? '#ef4444' : 'var(--text-muted)',
                                fontSize: 11
                            }}>
                                <CalendarOutlined />
                                {dayjs(task.deadline).fromNow()}
                            </div>
                        </Tooltip>
                    ) : (
                        <div />
                    )}

                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {/* Reminder indicator */}
                        {task.reminder_at && !task.reminder_sent && (
                            <Tooltip title={`Напоминание: ${dayjs(task.reminder_at).format('DD.MM.YYYY HH:mm')}`}>
                                <BellOutlined style={{ color: '#f59e0b', fontSize: 12 }} />
                            </Tooltip>
                        )}

                        {/* Assignee */}
                        {task.assigned_to_name && (
                            <Tooltip title={task.assigned_to_name}>
                                <Avatar
                                    size={22}
                                    style={{
                                        background: '#667eea',
                                        fontSize: 10
                                    }}
                                >
                                    {task.assigned_to_name.charAt(0).toUpperCase()}
                                </Avatar>
                            </Tooltip>
                        )}
                    </div>
                </div>
            </div>
        )
    }

    // Task View Modal
    const TaskViewModal = () => {
        if (!selectedTask) return null

        const statusConfig = STATUS_CONFIG[selectedTask.status as keyof typeof STATUS_CONFIG]
        const priorityConfig = PRIORITY_CONFIG[selectedTask.priority as keyof typeof PRIORITY_CONFIG]

        return (
            <Modal
                title={null}
                open={viewModalVisible}
                onCancel={() => setViewModalVisible(false)}
                footer={[
                    <Button key="edit" icon={<EditOutlined />} onClick={() => {
                        setViewModalVisible(false)
                        openEditModal(selectedTask)
                    }}>
                        Редактировать
                    </Button>,
                    <Button key="close" type="primary" onClick={() => setViewModalVisible(false)}>
                        Закрыть
                    </Button>
                ]}
                width={600}
            >
                <div style={{ padding: '10px 0' }}>
                    {/* Header with priority and status */}
                    <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                        <Tag color={priorityConfig.color} style={{ borderRadius: 6 }}>
                            {priorityConfig.label}
                        </Tag>
                        <Tag
                            icon={statusConfig.icon}
                            color={statusConfig.color}
                            style={{ borderRadius: 6 }}
                        >
                            {statusConfig.label}
                        </Tag>
                    </div>

                    {/* Title */}
                    <h2 style={{
                        fontSize: 24,
                        fontWeight: 700,
                        color: 'var(--text-primary)',
                        marginBottom: 16
                    }}>
                        {selectedTask.title}
                    </h2>

                    {/* Description */}
                    {selectedTask.description && (
                        <div style={{
                            background: 'var(--bg-hover)',
                            borderRadius: 12,
                            padding: 16,
                            marginBottom: 20
                        }}>
                            <div style={{
                                fontSize: 12,
                                color: 'var(--text-muted)',
                                marginBottom: 8,
                                textTransform: 'uppercase',
                                fontWeight: 600
                            }}>
                                Описание
                            </div>
                            <div style={{
                                color: 'var(--text-secondary)',
                                lineHeight: 1.6
                            }}>
                                {selectedTask.description}
                            </div>
                        </div>
                    )}

                    <Divider style={{ margin: '16px 0' }} />

                    {/* Info Grid */}
                    <Row gutter={[16, 16]}>
                        {selectedTask.assigned_to_name && (
                            <Col span={12}>
                                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>
                                    Исполнитель
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <Avatar size={24} style={{ background: '#667eea' }}>
                                        {selectedTask.assigned_to_name.charAt(0).toUpperCase()}
                                    </Avatar>
                                    <span style={{ fontWeight: 500 }}>{selectedTask.assigned_to_name}</span>
                                </div>
                            </Col>
                        )}

                        {selectedTask.created_by_name && (
                            <Col span={12}>
                                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>
                                    Создал
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <Avatar size={24} style={{ background: '#10b981' }}>
                                        {selectedTask.created_by_name.charAt(0).toUpperCase()}
                                    </Avatar>
                                    <span style={{ fontWeight: 500 }}>{selectedTask.created_by_name}</span>
                                </div>
                            </Col>
                        )}

                        {selectedTask.deadline && (
                            <Col span={12}>
                                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>
                                    Дедлайн
                                </div>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    color: dayjs(selectedTask.deadline).isBefore(dayjs()) && selectedTask.status !== 'completed'
                                        ? '#ef4444'
                                        : 'var(--text-primary)'
                                }}>
                                    <CalendarOutlined />
                                    <span style={{ fontWeight: 500 }}>
                                        {dayjs(selectedTask.deadline).format('DD.MM.YYYY HH:mm')}
                                    </span>
                                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                                        ({dayjs(selectedTask.deadline).fromNow()})
                                    </span>
                                </div>
                            </Col>
                        )}

                        {selectedTask.reminder_at && (
                            <Col span={12}>
                                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>
                                    Напоминание
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <BellOutlined style={{ color: '#f59e0b' }} />
                                    <span style={{ fontWeight: 500 }}>
                                        {dayjs(selectedTask.reminder_at).format('DD.MM.YYYY HH:mm')}
                                    </span>
                                    {selectedTask.reminder_sent && (
                                        <Tag color="green" style={{ margin: 0 }}>Отправлено</Tag>
                                    )}
                                </div>
                            </Col>
                        )}

                        <Col span={12}>
                            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>
                                Создано
                            </div>
                            <div style={{ fontWeight: 500 }}>
                                {dayjs(selectedTask.created_at).format('DD.MM.YYYY HH:mm')}
                            </div>
                        </Col>

                        {selectedTask.completed_at && (
                            <Col span={12}>
                                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>
                                    Завершено
                                </div>
                                <div style={{ fontWeight: 500, color: '#10b981' }}>
                                    {dayjs(selectedTask.completed_at).format('DD.MM.YYYY HH:mm')}
                                </div>
                            </Col>
                        )}
                    </Row>

                    {/* Quick status change */}
                    <Divider style={{ margin: '20px 0 16px' }} />
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
                        Быстро изменить статус:
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                            <Button
                                key={key}
                                type={selectedTask.status === key ? 'primary' : 'default'}
                                icon={cfg.icon}
                                onClick={async () => {
                                    if (selectedTask.status !== key) {
                                        await handleStatusChange(selectedTask, key)
                                        setSelectedTask({ ...selectedTask, status: key })
                                    }
                                }}
                                style={{
                                    background: selectedTask.status === key ? cfg.color : undefined,
                                    borderColor: cfg.color,
                                    color: selectedTask.status === key ? 'white' : cfg.color
                                }}
                            >
                                {cfg.label}
                            </Button>
                        ))}
                    </div>
                </div>
            </Modal>
        )
    }

    return (
        <div style={{ padding: 24 }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 24
            }}>
                <div>
                    <h1 style={{
                        fontSize: 28,
                        fontWeight: 700,
                        color: 'var(--text-primary)',
                        margin: 0
                    }}>
                        Задачи
                    </h1>
                    <p style={{
                        color: 'var(--text-muted)',
                        margin: '4px 0 0'
                    }}>
                        Управляйте задачами команды • Перетаскивайте карточки между колонками
                    </p>
                </div>

                <div style={{ display: 'flex', gap: 12 }}>
                    {/* Filters */}
                    <Select
                        placeholder="Приоритет"
                        allowClear
                        style={{ width: 140 }}
                        value={filterPriority}
                        onChange={setFilterPriority}
                        suffixIcon={<FilterOutlined />}
                    >
                        {Object.entries(PRIORITY_CONFIG).map(([key, cfg]) => (
                            <Select.Option key={key} value={key}>
                                <Tag color={cfg.color} style={{ margin: 0 }}>{cfg.label}</Tag>
                            </Select.Option>
                        ))}
                    </Select>

                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => {
                            setEditingTask(null)
                            form.resetFields()
                            setModalVisible(true)
                        }}
                        style={{
                            background: 'var(--brand-primary)',
                            border: 'none',
                            height: 40,
                            paddingLeft: 20,
                            paddingRight: 20,
                            boxShadow: '0 4px 14px 0 rgba(0,118,255,0.39)'
                        }}
                    >
                        Новая задача
                    </Button>
                </div>
            </div>

            {/* Kanban Board */}
            <Spin spinning={loading}>
                <Row gutter={16}>
                    {Object.entries(STATUS_CONFIG).map(([status, config]) => (
                        <Col key={status} xs={24} sm={12} lg={6}>
                            <KanbanColumn status={status} config={config} />
                        </Col>
                    ))}
                </Row>
            </Spin>

            {/* View Modal */}
            <TaskViewModal />

            {/* Create/Edit Modal */}
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {editingTask ? <EditOutlined style={{ color: '#667eea' }} /> : <PlusOutlined style={{ color: '#667eea' }} />}
                        <span>{editingTask ? 'Редактировать задачу' : 'Новая задача'}</span>
                    </div>
                }
                open={modalVisible}
                onCancel={() => {
                    setModalVisible(false)
                    setEditingTask(null)
                    form.resetFields()
                }}
                footer={null}
                width={550}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleCreateOrUpdate}
                    initialValues={{
                        priority: 'medium',
                        status: 'pending',
                        assigned_to_id: user?.id
                    }}
                    style={{ marginTop: 20 }}
                >
                    <Form.Item
                        name="title"
                        label="Название"
                        rules={[{ required: true, message: 'Введите название' }]}
                    >
                        <Input
                            placeholder="Что нужно сделать?"
                            size="large"
                        />
                    </Form.Item>

                    <Form.Item name="description" label="Описание">
                        <Input.TextArea
                            rows={3}
                            placeholder="Детали задачи..."
                        />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="priority" label="Приоритет">
                                <Select size="large">
                                    {Object.entries(PRIORITY_CONFIG).map(([key, cfg]) => (
                                        <Select.Option key={key} value={key}>
                                            <Tag color={cfg.color}>{cfg.label}</Tag>
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="status" label="Статус">
                                <Select size="large">
                                    {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                                        <Select.Option key={key} value={key}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <div style={{
                                                    width: 8,
                                                    height: 8,
                                                    borderRadius: '50%',
                                                    background: cfg.color
                                                }} />
                                                {cfg.label}
                                            </div>
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="assigned_to_id" label="Исполнитель">
                        <Select size="large" placeholder="Выберите исполнителя">
                            {operators.map((op: any) => (
                                <Select.Option key={op.id} value={op.id}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <Avatar size={20} style={{ background: '#667eea' }}>
                                            {(op.full_name || op.username).charAt(0).toUpperCase()}
                                        </Avatar>
                                        {op.full_name || op.username}
                                    </div>
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="deadline" label="Дедлайн">
                                <DatePicker
                                    showTime
                                    format="DD.MM.YYYY HH:mm"
                                    style={{ width: '100%' }}
                                    size="large"
                                    placeholder="Срок выполнения"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="reminder_at" label="Напоминание">
                                <DatePicker
                                    showTime
                                    format="DD.MM.YYYY HH:mm"
                                    style={{ width: '100%' }}
                                    size="large"
                                    placeholder="Когда напомнить"
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
                        <Button
                            type="primary"
                            htmlType="submit"
                            block
                            size="large"
                            style={{
                                background: 'var(--brand-primary)',
                                border: 'none',
                                height: 50,
                                fontSize: 16,
                                fontWeight: 600,
                                boxShadow: '0 4px 14px 0 rgba(0,118,255,0.39)'
                            }}
                        >
                            {editingTask ? 'Сохранить изменения' : 'Создать задачу'}
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Drag Ghost Overlay */}
            {draggedTask && dragPosition && (
                <div
                    style={{
                        position: 'fixed',
                        left: dragPosition.x - 100,
                        top: dragPosition.y - 30,
                        width: 200,
                        padding: '12px 14px',
                        background: '#FFF3B0',
                        border: '1px solid rgba(230, 190, 60, 0.7)',
                        borderRadius: 12,
                        boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                        pointerEvents: 'none',
                        zIndex: 10000,
                        transform: 'rotate(-3deg)',
                        opacity: 0.95
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                        <DragOutlined style={{ color: '#999', fontSize: 12 }} />
                        <Tag color={PRIORITY_CONFIG[draggedTask.priority as keyof typeof PRIORITY_CONFIG]?.color || 'blue'} style={{ margin: 0 }}>
                            {PRIORITY_CONFIG[draggedTask.priority as keyof typeof PRIORITY_CONFIG]?.label || draggedTask.priority}
                        </Tag>
                    </div>
                    <div style={{
                        fontWeight: 600,
                        color: '#333',
                        fontSize: 14,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                    }}>
                        {draggedTask.title}
                    </div>
                </div>
            )}
        </div>
    )
}
